import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import logoMark from "../assets/releaf_mark.png";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user && user.role !== "patient") return <Navigate to="/admin" replace />;
  if (!loading && user?.role === "patient") return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(apiError(err, "Login failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center bg-ivory px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm"
        >
          <Link to="/" data-testid="admin-login-logo" className="flex items-center gap-3">
            <img src={logoMark} alt="Releaf monogram" className="h-12 w-auto" />
            <span>
              <span className="block font-serif text-xl text-forest">Releaf</span>
              <span className="block text-[9px] font-semibold uppercase tracking-[0.3em] text-gold-dark">Provider Portal</span>
            </span>
          </Link>

          <h1 className="mt-10 font-serif text-3xl text-forest">Welcome back</h1>
          <p className="mt-2 text-sm text-moss">Sign in to manage bookings and availability.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="admin-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-moss">Password</label>
                <Link to="/reset-password" data-testid="admin-forgot-password-link" className="text-xs font-medium text-moss transition-colors hover:text-gold-dark">Forgot password?</Link>
              </div>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-md border border-linen bg-ivory px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="w-full rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-forest-dark disabled:opacity-50"
            >
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <Link to="/" data-testid="admin-login-back" className="mt-8 inline-block text-sm font-medium text-moss transition-colors hover:text-gold-dark">
            &larr; Back to the website
          </Link>
        </motion.div>
      </div>

      <div className="relative hidden bg-forest lg:block">
        <div className="absolute bottom-16 left-12 right-12">
          <p className="font-serif text-4xl font-medium text-ivory">Confidence, delivered naturally.</p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-ivory/80">
            Injectables &middot; Hormone Therapy &middot; Wellness
          </p>
        </div>
      </div>
    </main>
  );
}
